import { useEffect, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { optionsChannels } from '../constants/data.constants';
import ChannelSms from './channels/channel-sms.component';
import ChannelRcs from './channels/channel-rcs.component';
import ChannelWhatsapp from './channels/channel-wa-component';
import Title from '../../../../components/forms/titles.component';
import Icon from '../../../../components/forms/icon.component';
import Radio from '../../../../components/forms/advance-radio-2.component';
import Select from 'react-select';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import ChannelEmail from './channels/channel-email.component';

function Stepper4({ onValidationChange }: { onValidationChange: (isValid: boolean) => void }) {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const [formatWarning, setFormatWarning] = useState<string | null>(null);

  const {
    channel: channels,
    masks: mask,
    complementsSwitch: complementsActive,
    complements: complementsSelected,
    phone_number: phoneNumber,
    msgWhatsAppLink: messageWaLink,
    urlLink: link,
    msgSMS: message,
    accountsWa: accountWa,
    templatesWa: templateWa,
    typeRCS: typeRcs,
    agentsRCS: agentsRcs,
    templatesRCS: templateRcs,
    accountsEmail: accountsEmail,
    templatesEmail: templatesEmail,
    subjectEmail: subjectEmail,
    channelsAvailable,
    headersSelects: optionsHeaders,
    headers,
    headersOptions: optionsExamples
  } = watch();

  const filteredOptionsChannels = optionsChannels.filter((option: any) => {
    if (option.value === 'wa') return channelsAvailable.includes('whatsapp');
    if (option.value === 'rcs') return channelsAvailable.some((key: any) => key.startsWith('rcs'));
    return channelsAvailable.includes(option.value);
  });

  const getColumnType = (channel: string) => {
    switch (channel) {
      case 'sms':
      case 'wa':
      case 'rcs':
        return 'numero';
      case 'email':
        return 'correo';
      default:
        return 'numero';
    }
  };

  const getColumnText = (channel: string) => {
    const columnType = getColumnType(channel);
    return {
      label: columnType === 'numero' ? 'Columna de números' : 'Columna de correos',
      placeholder:
        columnType === 'numero'
          ? 'Selecciona la columna que contiene los números telefónicos'
          : 'Selecciona la columna que contiene los correos electrónicos'
    };
  };

  const validateEmailFormat = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneFormat = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

    const phoneRegex = /^\+?[0-9]{8,15}$/;
    return phoneRegex.test(cleanPhone);
  };

  const validateSampleData = (selectedColumn: string, channelType: string) => {
    if (!optionsExamples || !selectedColumn) {
      setFormatWarning(null);
      return;
    }

    const sampleRow = optionsExamples.find((row: any) => row.header === selectedColumn);
    const sampleValue = sampleRow?.value?.toString().trim();

    if (!sampleValue) {
      setFormatWarning(null);
      return;
    }

    const columnType = getColumnType(channelType);
    let isValidFormat = false;
    let warningMessage = '';

    if (columnType === 'correo') {
      isValidFormat = validateEmailFormat(sampleValue);
      warningMessage = isValidFormat
        ? ''
        : `El dato de ejemplo "${sampleValue}" no parece tener formato de correo electrónico válido.`;
    } else if (columnType === 'numero') {
      isValidFormat = validatePhoneFormat(sampleValue);
      warningMessage = isValidFormat
        ? ''
        : `El dato de ejemplo "${sampleValue}" no parece tener formato de número telefónico válido.`;
    }

    setFormatWarning(warningMessage);
  };

  useEffect(() => {
    if (headers && channels) {
      validateSampleData(headers, channels);
    } else {
      setFormatWarning(null);
    }
  }, [headers, channels, optionsExamples]);

  const stepper4Valid = (() => {
    if (!channels) return false;
    if (!headers) return false;

    if (optionsExamples && headers) {
      const sampleRow = optionsExamples.find((row: any) => row.header === headers);
      const sampleValue = sampleRow?.value?.toString().trim();

      if (sampleValue) {
        const columnType = getColumnType(channels);
        let isValidFormat = false;

        if (columnType === 'correo') {
          isValidFormat = validateEmailFormat(sampleValue);
        } else if (columnType === 'numero') {
          isValidFormat = validatePhoneFormat(sampleValue);
        }
        if (!isValidFormat) return false;
      }
    }

    switch (channels) {
      case 'sms': {
        const baseValidation = !!mask && !!message;

        if (!complementsActive) return baseValidation;

        const isComplementSelected = !!complementsSelected;
        const complementValidators: Record<string, boolean> = {
          WHATSAPP_LINK: !!phoneNumber && !!messageWaLink,
          LINK: !!link
        };

        const isComplementValid = complementValidators[complementsSelected] ?? true;

        return baseValidation && isComplementSelected && isComplementValid;
      }

      case 'rcs':
        return !!typeRcs && !!agentsRcs && !!templateRcs;

      case 'wa':
        return !!accountWa && !!templateWa;

      case 'email':
        return !!accountsEmail && !!templatesEmail && !!subjectEmail;

      default:
        return false;
    }
  })();

  useEffect(() => {
    onValidationChange(stepper4Valid);
  }, [stepper4Valid]);

  const columnText = channels
    ? getColumnText(channels)
    : { label: 'Columna', placeholder: 'Selecciona una columna' };

  return (
    <>
      <div className="text-left mb-3">
        <Title
          icon="ki-send"
          title={'Canal de envío'}
          subtitle={'Ve las diferentes configuraciones necesarias de tu canal.'}
          type="info"
        />
      </div>
      <div className="d-flex flex-stack cursor-pointer mb-3">
        <div className="d-flex align-items-center">
          <span className="symbol symbol-label"></span>
          <div className="d-flex flex-column">
            <span className="fw-bolder fs-3 required">Canales</span>
            <span className="fs-6">Elige uno de los disponibles.</span>
          </div>
        </div>
      </div>
      <div className="row mb-3">
        <Controller
          control={control}
          name="channel"
          rules={{ required: 'Para poder procesar tu campaña es necesario registrar un canal' }}
          render={({ field, fieldState: { error } }) =>
            filteredOptionsChannels.length === 0 ? (
              <div className="text-center w-100">
                <img
                  src="/assets/media/illustrations/undraw/no-find.svg"
                  alt="No se encontraron canales"
                  style={{ maxWidth: '300px', margin: '0 auto' }}
                />
                <p className="mt-3 fs-3 text-primary fw-bolder">No tienes canales habilitados.</p>
              </div>
            ) : (
              <Radio {...field} options={filteredOptionsChannels} errors={error} />
            )
          }
        />
      </div>

      {channels && optionsHeaders && optionsHeaders.length > 0 && (
        <div className="col-md-12 z-index-4 mb-15">
          <div className="fv-row">
            <label className="form-label required">{columnText.label}</label>
            <Controller
              control={control}
              name="headers"
              rules={{
                required: `Es necesario seleccionar la columna de ${getColumnType(channels)}`
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Select
                    options={optionsHeaders}
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? t('general.no-options', { inputValue }) : t('general.no-options')
                    }
                    className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                    classNamePrefix="form-select-custom"
                    placeholder={columnText.placeholder}
                    onChange={(
                      selectedOption: {
                        value: string;
                        label: string;
                      } | null
                    ) => {
                      onChange(selectedOption ? selectedOption.value : null);
                    }}
                    value={
                      optionsHeaders.find(
                        (option: { value: string; label: string }) => option.value === value
                      ) || null
                    }
                  />
                  <InvalidFeedback error={error} />
                  {formatWarning && (
                    <div className="alert alert-dismissible bg-light-info border border-info d-flex border-dashed flex-column flex-sm-row p-5 mt-5 animate-fadeInUp animate-delay-1">
                      <Icon icon="ki-information" size="fs-2hx" type="info" />
                      <div className="d-flex flex-column pe-0 pe-sm-10 ms-5">
                        <h5 className="mb-1 text-info fw-bolder fs-3 mb-3">
                          {t('blacklist-page.warning-title')}
                        </h5>
                        <span>{formatWarning}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            />
          </div>
        </div>
      )}

      {channels === 'sms' && <ChannelSms />}
      {channels === 'rcs' && <ChannelRcs />}
      {channels === 'wa' && <ChannelWhatsapp />}
      {channels === 'email' && <ChannelEmail />}
    </>
  );
}

export default Stepper4;
